import { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Accordion } from '@sorare/core/src/atoms/layout/Accordion';
import { ExternalLink } from '@sorare/core/src/atoms/navigation/ExternalLink';
import { BodyS, LabelL } from '@sorare/core/src/atoms/typography';

const StyledUl = styled.ul`
  & > li {
    list-style-type: disc;
  }
`;

const messages = defineMessages({
  question1: {
    id: 'FirstLineupEdition.FAQ.Question1',
    defaultMessage: 'How does scoring work in Sorare Football?',
  },
  answer1: {
    id: 'FirstLineupEdition.FAQ.Answer1',
    defaultMessage: `Scoring in Sorare Football is a combination of two factors:{br} 
        <ul>
        <li>Every action a player does on the pitch influences his Player Score. <link>Check out the Scoring Matrix here!</link></li>
        <li>If a card has bonuses (XP, Captain, etc.), it will get more points.</li>
        </ul>
        Your team score is the sum of your 5 card scores, and determines your rank on the leaderboards
        `,
  },
  question2: {
    id: 'FirstLineupEdition.FAQ.Question2',
    defaultMessage:
      "Why didn't the recommended team include more star players?",
  },
  answer2: {
    id: 'FirstLineupEdition.FAQ.Answer2',
    defaultMessage:
      'Our recommended starter team helps you get started quickly with a strong, affordable lineup. Top stars can be more expensive, so we suggest high-potential players with good matchups.{br}But you can swap in your favorite players by editing the recommended team.',
  },
  question3: {
    id: 'FirstLineupEdition.FAQ.Question3',
    defaultMessage: 'What happens to my cards after I buy them?',
  },
  answer3: {
    id: 'FirstLineupEdition.FAQ.Answer3',
    defaultMessage:
      "Once you buy a card, you own it. It's yours to use as you wish! You can play it, sell it to other managers, or keep it as a collectible. After the season, it enters Classic mode. Still playable, but newer season cards will have added advantages.",
  },
  question4: {
    id: 'FirstLineupEdition.FAQ.Question4',
    defaultMessage: 'Where can I learn more?',
  },
  answer4: {
    id: 'FirstLineupEdition.FAQ.Answer4',
    defaultMessage:
      'Our brief video tutorials (five minutes total) will give you an overview to give you a head start in your Sorare journey: <link>https://sorare.com/blog/football/get-started-with-our-beginner-guide</link>',
  },
  question5: {
    id: 'FirstLineupEdition.FAQ.Question5',
    defaultMessage: 'How are card prices determined?',
  },
  answer5: {
    id: 'FirstLineupEdition.FAQ.Answer5',
    defaultMessage:
      'Sorare Card prices are set by the market. Sorare Managers buy and sell cards based on player performance, card rarity, and how useful the player is in competitions.',
  },
});

const questions = [
  {
    key: 1,
    messageDescriptor: messages.question1,
    answerMessageDescriptor: messages.answer1,
    values: {
      br: <br />,
      ul: (chunks: ReactNode[]) => <StyledUl>{chunks}</StyledUl>,
      li: (chunks: ReactNode[]) => <li>{chunks}</li>,
      link: (chunks: ReactNode[]) => (
        <ExternalLink href="https://sorare.com/help/a/4402897588241/-" inline>
          {chunks}
        </ExternalLink>
      ),
    },
  },
  {
    key: 2,
    messageDescriptor: messages.question2,
    answerMessageDescriptor: messages.answer2,
    values: {
      br: <br />,
    },
  },
  {
    key: 3,
    messageDescriptor: messages.question3,
    answerMessageDescriptor: messages.answer3,
  },
  {
    key: 4,
    messageDescriptor: messages.question4,
    answerMessageDescriptor: messages.answer4,
    values: {
      link: (chunks: ReactNode[]) => (
        <ExternalLink
          href="https://sorare.com/blog/football/get-started-with-our-beginner-guide"
          inline
        >
          {chunks}
        </ExternalLink>
      ),
    },
  },
  {
    key: 5,
    messageDescriptor: messages.question5,
    answerMessageDescriptor: messages.answer5,
  },
];

export const FAQ = () => {
  return (
    <div>
      {questions.map(
        (
          { key, messageDescriptor, answerMessageDescriptor, values },
          index
        ) => (
          <Accordion
            key={key}
            noHorizontalPadding
            noBorder={index === questions.length - 1}
            title={
              <LabelL>
                <FormattedMessage {...messageDescriptor} />
              </LabelL>
            }
          >
            <BodyS color="var(--c-nd-800)">
              <FormattedMessage {...answerMessageDescriptor} values={values} />
            </BodyS>
          </Accordion>
        )
      )}
    </div>
  );
};
